import matchPath from "./matchPath";
import ajax from "./ajax";
// import BaseApi from "./base-api";
import { AutoPopupNode } from "./components/auto-popup-component";
import context from "./context";

export const EVENT_NAME_NO_POPUP = "noPopup";
export const EVENT_NAME_ON_SUBMIT = "onSubmit";
export const EVENT_NAME_ON_CANCEL = "onCancel";

export const POPUP_MANAGER_STATE_IDLE = "idle";
export const POPUP_MANAGER_STATE_SHOWING_POPUPS = "showing";

export class PopupApi {
    constructor() { }

    /** @returns {PopupTO} */
    async getPopupByNameAndRevision(name, revision) {
        const result = await ajax.popup().get(`${name}/${revision}`);
        return result.data;
    }

    /** @returns {PopupTO[]} */
    async loadByRequestAsync(/** @type {PopupRequestTO} */request) {
        const result = await ajax.popup().post("load-by-request", request);
        return result.data;
    }

    async updateResponseAsync(/** @type {PopupResponseTO} */ response) {
        await ajax.popup().post("update-response", response);
    }
}

export class PopupTO {
    constructor() {
        this.doNotShowAgainKey = null;
        this.extraConfig = null;
        this.id = null;
        this.name = null;
        this.revision = null;
        this.size = null;
        this.template = null;
        this.title = null;
        this.type = null;
    }
}

export class PopupRequestTO {
    constructor(path) {
        this.openPath = path;
        this.popupFlags = {};
    }
}

export class PopupResponseTO {
    constructor() {
        this.path = null;
        this.popupName = null;
        this.revision = null;
        this.data = {};
    }
}

class PopupManager {
    constructor() {
        this._pathMap = [];
        this._isInit = false;

        this._noPopupListeners = [];
        this._onSubmitListeners = [];
        this._onCancelListeners = [];

        this._lastPopups = null;
        this._state = POPUP_MANAGER_STATE_IDLE;

        this.autoPopupNavListener = this.autoPopupNavListener.bind(this);
        this.isAutoPopupInit = this.isAutoPopupInit.bind(this);
        this.registerAutoPopupPath = this.registerAutoPopupPath.bind(this);
        this.findMatch = this.findMatch.bind(this);
        this.onPopupSubmit = this.onPopupSubmit.bind(this);
        this.onPopupCancel = this.onPopupCancel.bind(this);
        this.checkForPopupAsync = this.checkForPopupAsync.bind(this);
        this.setHistory = this.setHistory.bind(this);
    }

    getLastAutoPopups() {
        return this._lastPopups || [];
    }

    setHistory(/** @type {import("history").History} */history) {
        history.listen(this.autoPopupNavListener);
    }

    isAutoPopupInit() {
        return this._pathMap.length !== 0;
    }

    registerAutoPopupPath(path, exact, strict) {
        exact = !!exact;
        strict = !!strict;
        this._pathMap.push({
            path,
            exact,
            strict
        })
    }

    findMatch(pathname) {
        for (const path of this._pathMap) {
            const match = matchPath(pathname, path);
            if (match) return match;
        }
        return null;
    }

    /**
     * Should not be call directly
     * @param {*} location 
     * @param {*} action 
     */
    autoPopupNavListener(/** @type {import("history").Location} */location, /** @type {import("history").Action} */action) {
        this.checkForPopupAsync(location.pathname);
    };

    onNoPopupShow() {
        for (let i = 0; i < this._noPopupListeners.length; i++) this._noPopupListeners[i]();
    }

    onPopupSubmit(/**@type {PopupTO}*/ popup,/** @type{PopupResponseTO} */ data) {
        for (let i = 0; i < this._onSubmitListeners.length; i++) this._onSubmitListeners[i](popup, data);
    }

    onPopupCancel(/**@type {PopupTO}*/ popup,/** @type{PopupResponseTO} */ data) {
        for (let i = 0; i < this._onCancelListeners.length; i++) this._onCancelListeners[i](popup, data);
    }

    /**
     * 
     * @param {string} eventName 
     * @param {*} listener 
     */
    addListener(eventName, listener) {
        let queue = null;
        switch (eventName) {
            case EVENT_NAME_NO_POPUP:
                queue = this._noPopupListeners;
                break;
            case EVENT_NAME_ON_CANCEL:
                queue = this._onCancelListeners;
                break;
            case EVENT_NAME_ON_SUBMIT:
                queue = this._onSubmitListeners;
                break;
        }

        if (queue) {
            const index = queue.indexOf(listener);
            if (index < 0) queue.push(listener);
        }
    }

    /**
     * 
     * @param {string} eventName 
     * @param {*} listener 
     */
    removeListener(eventName, listener) {
        let queue = null;
        switch (eventName) {
            case EVENT_NAME_NO_POPUP:
                queue = this._noPopupListeners;
                break;
            case EVENT_NAME_ON_CANCEL:
                queue = this._onCancelListeners;
                break;
            case EVENT_NAME_ON_SUBMIT:
                queue = this._onSubmitListeners;
                break;
        }

        if (queue) {
            const index = queue.indexOf(listener);
            if (index > -1) queue.splice(index, 1);
        }
    }

    getState() {
        return this._state;
    }

    /**
     *      
     * @param {PopupResponseTO} response
     */
    async updateResponseAsync(response) {
        const api = new PopupApi();
        await api.updateResponseAsync(response);
    }

    /**
     * 
     * @param {PopupTO} popup 
     * @param {(popup: PopupTO, data: PopupResponseTO) => void} onSubmit 
     * @param {(popup: PopupTO, data: PopupResponseTO) => void} onCancel 
     */
    async showPopupAsync(/** @type {PopupTO} */popup, onSubmit, onCancel) {
        const node = new AutoPopupNode(popup, null);
        node.show(onSubmit, onCancel);
    }

    /**
     * 
     * @param {string} name 
     * @param {int} revision 
     * @param {(popup: PopupTO, data: PopupResponseTO) => void} onSubmit 
     * @param {(popup: PopupTO, data: PopupResponseTO) => void} onCancel
     */
    async showPopupByNameAndRevisionAsync(name, revision, onSubmit, onCancel) {
        const popup = await this.getPopupAsync(name, revision, onSubmit, onCancel);
        await this.showPopupAsync(popup);
    }

    /**
     * 
     * @param {string} name 
     * @param {number} revision 
     * 
     * @return {PopupTO}
     */
    async getPopupAsync(name, revision) {
        const api = new PopupApi();
        return await api.getPopupByNameAndRevision(name, revision);
    }

    async checkForPopupAsync(path) {
        if (!context.isAuthenticated()) return;
        try {
            const match = this.findMatch(path);
            if (match) {
                console.log("Find match: ", match);
                const api = new PopupApi();

                const request = new PopupRequestTO();
                request.openPath = path;

                const popups = await api.loadByRequestAsync(request);
                if (popups.length > 0) {
                    try {
                        //const root = new LinkListNode(popups[0]);
                        //let cur = root;
                        let cur = null;
                        let next = null;

                        for (let i = popups.length - 1; i >= 0; i--) {
                            cur = new AutoPopupNode(popups[i], next);
                            next = cur;
                        }
                        this._state = POPUP_MANAGER_STATE_SHOWING_POPUPS;
                        cur.show(path, this.onPopupSubmit, this.onPopupCancel);
                        this._lastPopups = popups || [];
                    } finally {
                        this._state = POPUP_MANAGER_STATE_IDLE;
                    }
                } else this.onNoPopupShow();
            }
        } catch (e) {
            console.error(e);
        }
    }
}

const popupManager = new PopupManager();

export default popupManager;